<template>
    <!-- A posted kit with no results yet: where it's up to and what to expect, as the epigenetics
         Overview shows for its kits -->
    <div class="anc-landing">
        <section class="anc-landing-hero">
            <div class="anc-landing-hero__text">
                <div class="anc-landing-hero__eyebrow">Kit {{ barcode }} · {{ label }}</div>
                <template v-if="failure">
                    <h1 class="anc-landing-hero__title">{{ failure.title }}</h1>
                    <p class="anc-landing-hero__lead">{{ failure.lead }}</p>
                </template>
                <template v-else>
                    <h1 class="anc-landing-hero__title">
                        Here's what to expect from your {{ hasAncestryTests ? "ancestry test" : "DNA kit" }}
                    </h1>
                    <p class="anc-landing-hero__lead">
                        Your kit is activated. Once the lab has analysed your sample, your results will appear
                        here, and we'll email you when they're ready.
                    </p>
                </template>
            </div>
            <img src="@/assets/world-map.png" alt="" class="anc-landing-hero__art">
        </section>

        <section class="anc-landing-card">
            <h2 class="anc-landing-title">Your kit's progress</h2>
            <p v-if="!failure" class="anc-landing-sub">
                Results usually take 6–8 weeks from when we receive your sample.
            </p>
            <p v-if="!failure && rawStatus === 'shipped' && viaUsFulfilment" class="anc-landing-note">
                Your sample goes to our fulfilment centre before it's sent on to the lab, so please allow up
                to 20 days for the first update.
            </p>
            <KitProgress :barcode="barcode" :raw-status="rawStatus" />
        </section>

        <section v-if="results.length" class="anc-landing-card">
            <h2 class="anc-landing-title">What your results will show</h2>
            <p class="anc-landing-sub">From the tests on this kit.</p>
            <ul class="anc-landing-results">
                <li v-for="result in results" :key="result.key" class="anc-landing-result">
                    <h3 class="anc-landing-result__title">{{ result.title }}</h3>
                    <p class="anc-landing-result__text">{{ result.text }}</p>
                </li>
            </ul>
        </section>

        <!-- Only while the sample is still with the customer -->
        <section v-if="rawStatus === 'shipped'" class="anc-landing-card">
            <h2 class="anc-landing-title">Sending your sample back</h2>
            <p class="anc-landing-text">
                Follow the instructions in your kit, then post your sample back to us as soon as you can. The
                steps above move on once it reaches the lab.
            </p>
            <p class="anc-landing-small">
                Not sure how to take your sample? Contact
                <a href="mailto:help@livingdna.com">help@livingdna.com</a>.
            </p>
        </section>
    </div>
</template>

<script setup>
import { computed } from "vue";
import store from "@/store";
import { kitLabel } from "@/services/kitService";
import { useAncestryKit } from "@/composables/useAncestryKit";
import KitProgress from "@/components/kit/KitProgress.vue";

const { barcode, test, rawStatus, failure, viaUsFulfilment, hasAncestryTests } = useAncestryKit();

const label = computed(() => kitLabel(barcode.value, test.value?.product_sku));

// Each result the kit can give, and the test on the kit that gives it
const RESULTS = [
    {
        key: "recent",
        test: "autosomal",
        title: "Recent Ancestry",
        text: "Where your ancestors lived in recent centuries, broken down into regions and subregions on a map.",
    },
    {
        key: "recentStarter",
        test: "autosomalStarter",
        title: "Recent Ancestry",
        text: "Where your ancestors lived in recent centuries, broken down by continent.",
    },
    {
        key: "motherline",
        test: "mtdna",
        title: "Motherline",
        text: "Your maternal haplogroup: the migration routes of your mother's direct line, and where it's found today.",
    },
    {
        key: "fatherline",
        test: "ydna",
        title: "Fatherline",
        text: "Your paternal haplogroup: the migration routes of your father's direct line, and where it's found today.",
    },
    {
        key: "familyMatching",
        test: "familyMatching",
        title: "Family Matching",
        text: "Relatives who have also tested with Living DNA, found by comparing your DNA with theirs.",
    },
    {
        key: "viking",
        test: "viking",
        title: "Viking ancestry",
        text: "How genetically similar you are to the Vikings.",
    },
    {
        key: "classical",
        test: "classical",
        title: "Classical ancestry",
        text: "How your DNA compares with the people of ancient Rome, Greece and Egypt.",
    },
    {
        key: "archaic",
        test: "archaic",
        title: "Neanderthal ancestry",
        text: "How much of your DNA comes from Neanderthals.",
    },
];

const results = computed(() => {
    const kitTests = store.getters.kitTests ?? {};
    const has = (type) => kitTests[type]?.purchased === true;
    // Genetic females have no Y chromosome, so no Fatherline result
    const noYChromosome = store.getters.profile?.profile?.gender === 2;
    return RESULTS.filter((result) => {
        if (!has(result.test)) return false;
        if (result.key === "recentStarter" && has("autosomal")) return false;
        if (result.key === "fatherline" && noYChromosome) return false;
        return true;
    });
});
</script>

<style lang="scss" scoped>
// The epigenetics Overview landing's look, so the two read as one product
$blue:      #154096;
$blue-soft: #E8EDF8;
$ink:       #1A1F36;
$ink-2:     #2E3A59;
$ink-3:     #4A5472;
$rule:      #E6E8EF;

.anc-landing {
    width: 100%;
    max-width: 1100px;
    margin: 24px auto 40px;
    text-align: left;
}

// ── Hero ─────────────────────────────────────────────────────────────────────
.anc-landing-hero {
    display: flex;
    align-items: center;
    gap: 32px;
    margin-bottom: 20px;
    padding: 40px 44px;
    border-radius: 20px;
    color: #fff;
    background: linear-gradient(120deg, #102F70 0%, $blue 55%, #3366CC 100%);
}

.anc-landing-hero__text { flex: 1 1 auto; max-width: 640px; }

.anc-landing-hero__eyebrow {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.75);
    margin-bottom: 12px;
    overflow-wrap: anywhere;
}

.anc-landing-hero__title {
    font-size: 30px;
    font-weight: 700;
    line-height: 1.25;
    margin: 0 0 14px;
    color: #fff;
}

.anc-landing-hero__lead {
    font-size: 16px;
    line-height: 1.65;
    margin: 0;
    color: rgba(255, 255, 255, 0.88);
}

.anc-landing-hero__art {
    flex: 0 0 auto;
    width: 260px;
    margin-left: auto;
    opacity: 0.9;
}

// ── Cards ────────────────────────────────────────────────────────────────────
.anc-landing-card {
    margin-bottom: 20px;
    padding: 24px;
    border-radius: 20px;
    background: #fff;
}

.anc-landing-title { font-size: 18px; font-weight: 700; color: $ink; margin: 0 0 6px; }
.anc-landing-sub   { font-size: 14px; color: $ink-3; margin: 0 0 18px; }
.anc-landing-text  { font-size: 15px; color: $ink-2; line-height: 1.6; margin: 0 0 10px; }
.anc-landing-small { font-size: 13px; color: $ink-3; margin: 14px 0 0; }

.anc-landing-small a { color: $blue; font-weight: 600; }

// A failed kit's progress has no headline under its title
.anc-landing-title + .kit-progress { margin-top: 16px; }

.anc-landing-note {
    margin: -8px 0 18px;
    padding: 12px 14px;
    border-radius: 12px;
    background: $blue-soft;
    font-size: 14px;
    line-height: 1.5;
    color: $ink-2;
}

// ── What your results will show ──────────────────────────────────────────────
.anc-landing-results {
    list-style: none;
    padding: 0;
    margin: 0;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
}

.anc-landing-result {
    padding: 20px;
    border-radius: 16px;
    background: #F7F8FC;
    border: 1px solid $rule;
    border-top: 4px solid $blue;
}

.anc-landing-result__title { font-size: 16px; font-weight: 700; color: $ink; margin: 0 0 6px; }
.anc-landing-result__text  { font-size: 14px; color: $ink-3; line-height: 1.55; margin: 0; }

@media (max-width: 991px) {
    .anc-landing-results { grid-template-columns: repeat(2, 1fr); }
}

@media (max-width: 767px) {
    .anc-landing { margin-top: 16px; }
    .anc-landing-hero { padding: 28px 24px; }
    .anc-landing-hero__title { font-size: 24px; }
    // The map is decoration: on a phone the kit's progress matters more than the space
    .anc-landing-hero__art { display: none; }
    .anc-landing-card { padding: 20px 16px; border-radius: 16px; }
}

@media (max-width: 575px) {
    .anc-landing-results { grid-template-columns: 1fr; gap: 12px; }
    .anc-landing-result { padding: 16px; }
}
</style>
